"use client";

import { Check, Link2, Share2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { SendToChatDrawer } from "@/components/share/send-to-chat-drawer";
import { useShortShareUrl } from "@/hooks/use-short-share-url";
import { buildShareUrl } from "@/lib/analytics/share-link";
import { absolutePublicAppUrl } from "@/lib/public-app-url";
import { cn } from "@/lib/utils";

/** The shareable menu page for an event (see `/menu`). */
export function eventMenuPath(
  hiiveSlug: string,
  eventSlug: string,
  sectionId?: number | null,
) {
  const path = `/hiives/${hiiveSlug}/events/${eventSlug}/menu`;
  return sectionId ? `${path}?section=${sectionId}` : path;
}

/**
 * Share an event's menu.
 *
 * "share" opens the app's own share sheet — the one events, hiives and feed
 * posts use: send to chats, copy link, and the system sheet one tap further
 * in — rather than jumping straight to the OS sheet, which made the menu the
 * one shareable surface that felt like a different app. Sent to a chat it
 * goes as a link message, which unfurls with the menu's own title, pitch and
 * branded image. Every outgoing link is the short `app.hiive.club/l/…` form
 * when it resolved (the sheet pre-warms it on open).
 *
 * "copy" always copies — for organisers handing the link to a partner. Its
 * short link is fetched on mount for the same reason the sheet pre-warms.
 */
export function MenuShareButton({
  path,
  title,
  text,
  mode = "share",
  label,
  className,
}: {
  path: string;
  title: string;
  text: string;
  /** "copy" always copies — for organisers handing the link to a partner. */
  mode?: "share" | "copy";
  label?: string;
  className?: string;
}) {
  const pageUrl = absolutePublicAppUrl(path);
  const longUrl = buildShareUrl(pageUrl, { surface: "event_menu" });
  const shortUrl = useShortShareUrl(mode === "copy" ? longUrl : null);
  const [copied, setCopied] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  const copy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
      toast.success("Menu link copied", { description: url });
    } catch {
      toast.message("Menu link", { description: url });
    }
  };

  const Icon = copied ? Check : mode === "copy" ? Link2 : Share2;
  return (
    <>
      <button
        type="button"
        onClick={() => {
          if (mode === "share") setSheetOpen(true);
          else void copy(shortUrl ?? longUrl);
        }}
        className={cn("inline-flex items-center gap-1.5", className)}
        aria-label={label ? undefined : "Share menu"}
      >
        <Icon className="size-4" aria-hidden />
        {label ?? null}
      </button>
      {mode === "share" ? (
        // The menu drawer hosts this button too. React bubbles a portal's
        // events through the component tree, so without this boundary a drag
        // in the share sheet also reaches the menu drawer's own handlers and
        // pulls it down from underneath.
        <span
          className="contents"
          onPointerDown={stopPropagation}
          onPointerMove={stopPropagation}
          onPointerUp={stopPropagation}
          onTouchStart={stopPropagation}
          onTouchMove={stopPropagation}
          onTouchEnd={stopPropagation}
        >
          <SendToChatDrawer
            open={sheetOpen}
            onOpenChange={setSheetOpen}
            payload={{ type: "link", url: pageUrl }}
            nativeShare={{ title, text, url: pageUrl, surface: "event_menu" }}
          />
        </span>
      ) : null}
    </>
  );
}

function stopPropagation(event: { stopPropagation: () => void }) {
  event.stopPropagation();
}
